/**
 * apiClient.ts
 *
 * Centralized axios instance for all calls from the Outlook add-in to the
 * Public API. Redux slices (matterSlice.ts, contactSlice.ts, tagSlice.ts,
 * communicationDetailSlice.ts, etc.) previously repeated the same pattern:
 *
 *   const token = await getTokenByLocalStorage();
 *   axios.get(API_BASE_URL + Path, { headers: { Authorization: `Bearer ${token}` } })
 *
 * This module replaces that duplicated pattern with one shared client that:
 *   1. Injects the current Access Token automatically (via tokenManager).
 *   2. On a 401 response, refreshes the token exactly once and retries the
 *      original request - the calling code (Redux thunks) doesn't need to
 *      know a refresh happened.
 *   3. Never touches localStorage for the token.
 *
 * Existing slices are migrated by swapping their local `axios.get(...)`
 * call for `apiClient.get(...)` and deleting their manual Authorization
 * header + token-fetch line. Response shape, request payloads, and all
 * endpoint paths (from src/config/config.tsx) are unchanged.
 */

import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../config/config";
import { getAccessToken, forceRefresh, clearSession } from "./tokenManager";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await getAccessToken();
  if (token) {
    // config.headers is always a populated AxiosHeaders instance by the time
    // a request interceptor runs in axios v1 - set() avoids the "{} isn't
    // assignable to AxiosRequestHeaders" type error that a plain
    // `config.headers = { ...config.headers, ... }` reassignment hits.
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

interface RetryableConfig extends AxiosRequestConfig {
  _clarraRetried?: boolean;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableConfig | undefined;

    const isAuthFailure = error.response?.status === 401;
    const alreadyRetried = originalRequest?._clarraRetried;

    if (isAuthFailure && originalRequest && !alreadyRetried) {
      originalRequest._clarraRetried = true;

      const newToken = await forceRefresh();
      if (newToken) {
        // AxiosHeaders.from() normalizes whatever's already on the config
        // (plain object, AxiosHeaders instance, or undefined) into a proper
        // AxiosHeaders instance, then set() adds/overwrites Authorization -
        // same reasoning as the request interceptor above.
        const headers = AxiosHeaders.from(originalRequest.headers ?? {});
        headers.set("Authorization", `Bearer ${newToken}`);
        originalRequest.headers = headers;
        return apiClient.request(originalRequest);
      }

      // Refresh token is gone/revoked server-side - there is no way to
      // recover silently. Clear local session state and let the caller's
      // existing catch block show its usual "please sign in again" UI.
      await clearSession();
    }

    return Promise.reject(error);
  }
);

export default apiClient;
